import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, markRaw, ref } from "vue";
import { useRouter } from "vue-router";
import { useLocalStorage } from "@vueuse/core";

import {
    createPortfolio,
    ModelPortfolios,
    Portfolio,
    PortfolioMetadata,
    PositionData
} from "@tmi/types/api/market-data";

import { AUTH_RESET_EVENT, useAuthStore } from "@/stores/Auth";
import { usePermissionsStore } from "@/stores/Permissions";
import { createLoadStatus } from "@/stores/load-status";

import { clientStateManager } from "@/services/error-services";
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import { closedPositionKey, openedPositionKey } from "@/types/products/product-definitions";
import type { ModelPortfolio, ModelPortfolioTradeAlert, PortfolioColumnDefinition } from "@tmi/types/portfolio";

import routeManager from "@/router/route-manager";
import { forbiddenQuery } from "@/router/forbidden-location";

import { modelPortfolioAvailableColumns } from "@/config/modelPortfolioAvailableColumns";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";

const logger = createDefaultLogger();

// Constant value references
const availablePortfolioHeaderItems: Readonly<Map<string, string>> = modelPortfolioAvailableColumns;

// Stores the list of all possible model portfolios available to the current user
const defaultModelPortfolios: ModelPortfolio[] = [];
const allModelPortfolios = useLocalStorage<ModelPortfolio[]>( 'model-portfolios', defaultModelPortfolios );

// This is a single string that is set to the currently selected model-portfolio key (e.g.; 'checkpoint-trader', 'monthly-trend-trader', etc. )
const selectedPortfolioKey = ref<string>( '' );

// This is a singular boolean that tracks whether the open or closed position is currently selected
const openPositionsSelected = ref<boolean>( true );

// NOTE: The following two state variables should not change all that often and so could be cached on a longer term basis
const openPositionHeaderItems = ref<string[]>( [] );
const closedPositionHeaderItems = ref<string[]>( [] );

// UX roadmap #9: when the portfolios above were last fetched (epoch ms, 0 = never), persisted beside them. As an
// in-memory ref it was null after every reload, so the cached copy never counted as fresh and every hard load refetched.
// User-scoped: purged with 'model-portfolios' (USER_SCOPED_PURGE_KEYS).
const lastUpdate = useLocalStorage<number>( 'model-portfolios-updated-at', 0 );
const updateRefreshCoolOffMs = 3 * 60 * 1000;

// Stale-while-revalidate: having a copy to show and that copy being recent are separate questions. The screen shows
// any copy it has; only a copy that is both present and fresh skips the refresh.
const hasData = computed<boolean>( () => ( allModelPortfolios.value?.length ?? 0 ) > 0 );

// A function, not a computed: a computed over the clock caches its first answer and never expires. A stamp from the
// future (a clock moved back) counts as stale rather than fresh forever.
function isFresh(): boolean {
    const age = Date.now() - lastUpdate.value;
    return lastUpdate.value > 0 && age >= 0 && age < updateRefreshCoolOffMs;
}

const isLoading = computed<boolean>( () => loading.value );
const loading = ref<boolean>( false );
const error = ref<string>( '' );
// data-12 / errors-4: the last refresh's outcome. A timeout or a dropped connection used to resolve null and only log a
// warning, so a first visit that failed read "No positions" with no retry. `error` above stays the message for its
// existing readers.
const portfoliosLoad = createLoadStatus();

// The refresh in flight, joined by every caller that arrives while it runs (a plain `loading` guard resolved them at
// once against a store that was still empty). `generation` is bumped by a reset so a fetch that started for the
// previous member never writes its result after a sign-out.
let refresh: Promise<void> | null = null;
let generation = 0;

function dropUserScopedState(): void {
    generation += 1;
    refresh = null;
    loading.value = false;
    lastUpdate.value = 0;
    allModelPortfolios.value = defaultModelPortfolios;
    portfoliosLoad.reset();
}

// LD1 coherence: Auth.reset() purges localStorage natively (not observed by same-document VueUse refs) and dispatches
// this event, so the in-memory copy and its timestamp die with the token instead of counting as fresh for the next member.
if ( typeof window !== 'undefined' ) {
    window.addEventListener( AUTH_RESET_EVENT, dropUserScopedState );
}

export const useModelPortfoliosStore = defineStore( 'model-portfolio', () => {
    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );
    const permissionsStore = usePermissionsStore();

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        closedPositionHeaderItems.value = [];
        error.value = '';
        openPositionHeaderItems.value = [];
        openPositionsSelected.value = true;
        selectedPortfolioKey.value = '';

        // Clears the local storage copy and its timestamp, and orphans any fetch still in flight
        dropUserScopedState();
    }

    /**
     * Loads the member's model portfolios unless a fresh copy is already cached. Never rejects.
     *
     * Stale-while-revalidate (UX roadmap #9): a cached copy is shown at once and a stale one is refreshed behind it,
     * so nothing on the boot path waits for this call. Concurrent callers join the refresh in flight.
     */
    function initializeAsync(): Promise<void> {
        // If not authenticated, return immediately
        if ( !isAuthenticated.value ) {
            logger.debug( 'Exiting - not authenticated...' );
            return Promise.resolve();
        }

        if ( refresh ) {
            logger.debug( 'Joining the refresh already in flight...' );
            return refresh;
        }

        if ( hasData.value && isFresh() ) {
            logger.debug( 'Exiting - refresh window is not available yet...' );
            return Promise.resolve();
        }

        if ( !permissionsStore.accessibleModelPortfoliosSlugs || permissionsStore.accessibleModelPortfoliosSlugs.length === 0 ) {
            logger.debug( 'Exiting - no model portfolio permissions...' );
            return Promise.resolve();
        }

        const run: Promise<void> = refreshAsync( generation ).finally( () => {
            if ( refresh === run ) {
                refresh = null;
            }
        } );
        refresh = run;

        return run;
    }

    async function refreshAsync( runGeneration: number ): Promise<void> {
        lastUpdate.value = Date.now();

        loading.value = true;
        error.value = '';
        portfoliosLoad.start();

        try {
            const productSlugs = permissionsStore.accessibleModelPortfoliosSlugs.join( '|' );

            const result =
                await goFetch<ModelPortfolios, PersistedQueryPayload>( {
                    serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
                    endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
                    payload: {
                        id: '19b4de02201f88675c2f14725c970b9e',
                        variables: {
                            permissionNameHash: productSlugs
                        }
                    },
                    authToken: authToken.value,
                    // A failure rejects (to the catch below) instead of resolving null, which only logged a warning.
                    // The cooldown still applies: lastUpdate was stamped above.
                    forceThrowExceptions: true
                } );

            // A reset (sign-out, account switch) while this ran: the result belongs to the previous member.
            if ( runGeneration !== generation ) {
                return;
            }

            if ( result?.data ) {
                // cache only the products that are marked as "display-able"
                const response: ModelPortfolios = result.data as ModelPortfolios;
                if ( null != response?.modelPortfoliosByUserAccess?.portfolios ) {
                    const portfoliosReceived = response?.modelPortfoliosByUserAccess?.portfolios;
                    const portfolios: ModelPortfolio[] = portfoliosReceived.map( ( mp: Portfolio ) => {
                        const portfolioKey: string = mp.key;
                        const configuration: PortfolioMetadata = mp.metadata;

                        // NOTE: HACK ALERT - this is only until we can drive the MP by a pure data-driven engine
                        configuration.open_pos_keys =
                            [ ...configuration.open_pos_keys.filter( ( key: string ) => key !== 'first_check_point' && key !== 'next_check_point' ) ];

                        const portfolioData: PositionData = mp.data;
                        return createPortfolio( portfolioKey, configuration, portfolioData );
                    } );

                    allModelPortfolios.value = [ ...portfolios ];
                }
            }
            portfoliosLoad.succeed();
        } catch ( err: any ) {
            if ( runGeneration !== generation ) {
                return;
            }

            // Keep the copy on screen. This used to call reset(), so one timed-out refresh emptied a member's
            // portfolios (and their localStorage copy) until the next successful fetch.
            logger.warn( 'Model portfolios refresh failed; keeping the cached copy', err );
            error.value = `${ err?.message ?? err }`;
            portfoliosLoad.fail( err );
            clientStateManager.queueClientError( err, {} );
        } finally {
            // reset() already cleared the flag, and a later refresh may have set it again.
            if ( runGeneration === generation ) {
                loading.value = false;
            }
        }
    }

    const activePortfolio =
        computed<ModelPortfolio | undefined>( () =>
            ( allModelPortfolios.value ?? [] )
                .find( ( portfolio: ModelPortfolio ): boolean => portfolio.key === selectedPortfolioKey.value ) );
    const productSlug = computed<string>( () => selectedPortfolioKey.value );

    // `.value`: the ref itself is always truthy, so this said "opened" even while closed positions were shown, and the
    // position tabs, which start from it, disagreed with the store once the choice survives a re-selection (above).
    const activeView = computed<string>( () => openPositionsSelected.value ? openedPositionKey : closedPositionKey );
    // Derived rather than copied at selection time, so portfolios that land after the selection (a refresh behind the
    // cached copy, or a first fetch) reach the table without anyone selecting again (UX roadmap #9).
    const activeHeaders = computed<PortfolioColumnDefinition[]>( () =>
        openPositionsSelected.value ? retrieveOpenHeaders() : retrieveClosedHeaders() );
    const activePositions = computed<ModelPortfolioTradeAlert[]>( () =>
        ( openPositionsSelected.value ? activePortfolio.value?.data.positions.opened : activePortfolio.value?.data.positions.closed ) ?? [] );
    const portfolioTitle = computed<string>( () => activePortfolio.value?.configuration.name ?? 'Unknown' );

    const hasError = computed<boolean>( () => 'error' === portfoliosLoad.status.value );

    /**
     * Used to assign the selected product's model portfolios
     *
     * @param {string} productSlug - the slug for the product wishing to be set to active
     * reset to display the open positions table upon becoming active
     */
    function setCurrentModelPortfolio( productSlug: string ): void {
        selectedPortfolioKey.value = productSlug;
        adjustSelectedPositions( openedPositionKey );
    }

    /**
     * Used to switch between Open/Closed positions
     */
    function adjustSelectedPositions( key: string ) {
        openPositionsSelected.value = key === openedPositionKey;
    }

    const router = markRaw( useRouter() );

    // Used when switching between different products - e.g.; checkpoint-trader -> deep-market-trader
    function switchPortfolioContext( portfolioKey: string, displayOpenPositions: boolean = true ): void {
        const productHasModelPortfolio: ModelPortfolio | undefined =
            ( allModelPortfolios.value ?? [] ).find( mp => mp.key === portfolioKey?.toLowerCase() );
        const hasPermission: boolean = permissionsStore.hasModelPortfolioPermissionsForProduct( portfolioKey );
        if ( productHasModelPortfolio && !hasPermission ) {
            // UX roadmap #50: name the product, so the 403 page can say what includes this portfolio. Lower-cased like the
            // lookup above: Products.productBySlug matches exactly.
            router.push( { path: routeManager.forbiddenPath(), query: forbiddenQuery( { product: portfolioKey?.toLowerCase(), reason: 'locked' } ) } ).then();
            return;
        }

        // UX roadmap #9:
        //  - a permitted portfolio that is not fetched yet (first visit, or a purged cache) is selected anyway, and the
        //    derived headers and positions fill in when the fetch lands.
        //  - re-selecting the portfolio already selected keeps the member's open/closed choice. ProductWorkspace calls
        //    this again whenever the portfolios change, so a refresh landing behind the cached copy flipped a member
        //    looking at Closed positions back to Open.
        if ( hasPermission && selectedPortfolioKey.value !== portfolioKey ) {
            openPositionsSelected.value = displayOpenPositions;
            setCurrentModelPortfolio( portfolioKey );
        }
    }

    // Used to load the "Open Positions" headers for the currently selected model portfolio
    function retrieveOpenHeaders(): PortfolioColumnDefinition[] {
        const fieldData: Map<string, string> = availablePortfolioHeaderItems;
        const availableKeys: string[] = activePortfolio.value?.configuration?.openPositionKeyList ?? [];
        return availableKeys.reduce( ( current: any, key: string ) => [ ...current, { text: fieldData.get( key ), value: key } ], [] );
    }

    // Used to load the "Closed Positions" headers for the currently selected model portfolio
    function retrieveClosedHeaders(): PortfolioColumnDefinition[] {
        const fieldData: Map<string, string> = availablePortfolioHeaderItems;
        const availableKeys: string[] = activePortfolio.value?.configuration?.closedPositionKeyList ?? [];
        return availableKeys.reduce( ( current: any, key: string ) => [ ...current, { text: fieldData.get( key ), value: key } ], [] );
    }

    return {
        // ACTIONS
        adjustSelectedPositions,
        initializeAsync,
        reset,
        switchPortfolioContext,

        // GETTERS
        // Read-only view over every model portfolio the user can see (product slug = key).
        // `useLocalStorage` can hold null after reset(); callers always get an array.
        allModelPortfolios: computed<ModelPortfolio[]>( () => allModelPortfolios.value ?? [] ),
        activeHeaders,
        activePositions,
        activeView,
        error,
        hasData,
        hasError,
        isLoading,
        loadError: portfoliosLoad.error,
        loadFailure: portfoliosLoad.failure,
        loadStatus: portfoliosLoad.status,
        portfolioTitle,
        productSlug
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useModelPortfoliosStore, import.meta.hot ) );
}
